export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
export const DRIVE_READ_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
export const DRIVE_SCOPES = `${DRIVE_SCOPE} ${DRIVE_READ_SCOPE}`;
const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || '';
let accessToken = '';
let accessTokenExpiresAt = 0;
let authorizedEmail = '';
let authorizationPromise = null;
let scriptPromise;
const recentPrivateQuestionBanks = new Map();
const driveFolderPromises = new Map();
const DRIVE_FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';
const APP_ROOT_FOLDER = 'Question Banks';
const QUESTION_BANK_FOLDER = 'Ngân hàng câu hỏi';
const PUBLISHED_PRACTICE_FOLDER = 'Đề đã phát hành';

function escapeDriveQueryValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function normalizedFolderPart(value, fallback) {
  return String(value || fallback).trim().replace(/[\\/]+/g, ' - ').slice(0, 120) || fallback;
}

async function findOrCreateFolder(name, parentId, folderKey) {
  const cacheKey = `${parentId || 'root'}:${folderKey}`;
  if (!driveFolderPromises.has(cacheKey)) {
    driveFolderPromises.set(cacheKey, (async () => {
      const clauses = [
        `trashed = false`,
        `mimeType = '${DRIVE_FOLDER_MIME_TYPE}'`,
        `name = '${escapeDriveQueryValue(name)}'`,
        `${parentId ? `'${escapeDriveQueryValue(parentId)}' in parents` : `'root' in parents`}`,
        `appProperties has { key='app' and value='question-banks' }`,
        `appProperties has { key='folderKey' and value='${escapeDriveQueryValue(folderKey)}' }`
      ];
      const params = new URLSearchParams({ q: clauses.join(' and '), spaces: 'drive', pageSize: '1', fields: 'files(id)' });
      const existingResponse = await driveFetch(`https://www.googleapis.com/drive/v3/files?${params}`);
      const existing = (await existingResponse.json()).files?.[0];
      if (existing) return existing.id;
      const metadata = {
        name,
        mimeType: DRIVE_FOLDER_MIME_TYPE,
        ...(parentId ? { parents: [parentId] } : {}),
        appProperties: { app: 'question-banks', targetType: 'folder', folderKey }
      };
      const createdResponse = await driveFetch('https://www.googleapis.com/drive/v3/files?fields=id', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(metadata)
      });
      return (await createdResponse.json()).id;
    })().catch(error => {
      driveFolderPromises.delete(cacheKey);
      throw error;
    }));
  }
  return driveFolderPromises.get(cacheKey);
}

async function ensureFolderPath(parts) {
  let parentId = '';
  let folderKey = '';
  for (const part of parts) {
    folderKey = folderKey ? `${folderKey}/${part.key}` : part.key;
    parentId = await findOrCreateFolder(part.name, parentId, folderKey);
  }
  return parentId;
}

function questionBankFolder(subject, grade) {
  const normalizedGrade = normalizedFolderPart(grade, 'Chưa xác định').replace(/^Lớp\s+/i, '');
  const gradeName = normalizedGrade === 'Chưa xác định' ? normalizedGrade : `Lớp ${normalizedGrade}`;
  const subjectName = normalizedFolderPart(subject, 'Chưa xác định môn');
  return ensureFolderPath([
    { name: APP_ROOT_FOLDER, key: 'root' },
    { name: QUESTION_BANK_FOLDER, key: 'question-banks' },
    { name: gradeName, key: `grade:${normalizedGrade}` },
    { name: subjectName, key: `subject:${subjectName}` }
  ]);
}

function publishedPracticeFolder(subject) {
  const subjectName = normalizedFolderPart(subject, 'Chưa xác định môn');
  return ensureFolderPath([
    { name: APP_ROOT_FOLDER, key: 'root' },
    { name: PUBLISHED_PRACTICE_FOLDER, key: 'published-practices' },
    { name: subjectName, key: `subject:${subjectName}` }
  ]);
}

async function moveFileToFolder(fileId, folderId, appProperties = null) {
  const currentResponse = await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=parents,appProperties`);
  const current = await currentResponse.json();
  const oldParents = (current.parents || []).filter(parent => parent !== folderId);
  const params = new URLSearchParams({ fields: 'id,name,modifiedTime,appProperties' });
  if (!(current.parents || []).includes(folderId)) params.set('addParents', folderId);
  if (oldParents.length) params.set('removeParents', oldParents.join(','));
  const response = await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(appProperties ? { appProperties: { ...(current.appProperties || {}), ...appProperties } } : {})
  });
  return response.json();
}

function loadGoogleIdentityServices() {
  if (globalThis.google?.accounts?.oauth2) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('drive_oauth_script_failed'));
    document.head.append(script);
  });
  return scriptPromise;
}

async function driveFetch(url, options = {}) {
  if (!accessToken || Date.now() >= accessTokenExpiresAt) {
    clearDriveAuthorization();
    throw new Error('drive_authorization_required');
  }
  const response = await fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${accessToken}` }
  });
  if (response.status === 401) {
    clearDriveAuthorization();
    throw new Error('drive_authorization_expired');
  }
  if (!response.ok) throw new Error(`drive_error_${response.status}`);
  return response;
}

function firebaseGoogleEmail(firebaseUser) {
  return firebaseUser?.providerData.find(provider => provider.providerId === 'google.com')?.email || firebaseUser?.email || '';
}

function normalizedEmail(value) {
  return String(value || '').trim().toLowerCase();
}

export function acceptDriveAuthorization(token, firebaseUser, expiresInSeconds = 3300) {
  if (!token) return false;
  const email = normalizedEmail(firebaseGoogleEmail(firebaseUser));
  if (!email) return false;
  accessToken = token;
  authorizedEmail = email;
  accessTokenExpiresAt = Date.now() + Math.max(60, Number(expiresInSeconds) || 3300) * 1000;
  return true;
}

async function authorizeDrive(firebaseUser) {
  if (!clientId) throw new Error('drive_oauth_not_configured');
  const firebaseEmail = normalizedEmail(firebaseGoogleEmail(firebaseUser));
  if (!firebaseEmail) throw new Error('drive_google_identity_required');
  await loadGoogleIdentityServices();
  const tokenResponse = await new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPES,
      login_hint: firebaseEmail,
      callback: response => response.error ? reject(new Error('drive_authorization_failed')) : resolve(response),
      error_callback: () => reject(new Error('drive_authorization_failed'))
    });
    client.requestAccessToken({ prompt: '' });
  });
  acceptDriveAuthorization(tokenResponse.access_token, firebaseUser, tokenResponse.expires_in);
  const about = await driveFetch('https://www.googleapis.com/drive/v3/about?fields=user(emailAddress)');
  const identity = await about.json();
  if (normalizedEmail(identity.user?.emailAddress) !== firebaseEmail) {
    clearDriveAuthorization();
    throw new Error('drive_account_mismatch');
  }
}

export async function requestDriveAuthorization(firebaseUser) {
  const firebaseEmail = normalizedEmail(firebaseGoogleEmail(firebaseUser));
  if (accessToken && Date.now() < accessTokenExpiresAt && authorizedEmail === firebaseEmail) return;
  clearDriveAuthorization();
  if (!authorizationPromise) {
    authorizationPromise = authorizeDrive(firebaseUser).finally(() => { authorizationPromise = null; });
  }
  return authorizationPromise;
}

export function clearDriveAuthorization() {
  accessToken = '';
  accessTokenExpiresAt = 0;
  authorizedEmail = '';
  driveFolderPromises.clear();
}

export function hasDriveAuthorization() {
  return Boolean(accessToken && Date.now() < accessTokenExpiresAt);
}

export async function createPracticeFile({ name, json, operationId, practiceSetId }) {
  const folderId = await publishedPracticeFolder(json.subject);
  const metadata = {
    name,
    mimeType: 'application/json',
    parents: [folderId],
    appProperties: {
      app: 'question-banks', operationId, targetType: 'practiceSet', targetId: practiceSetId, operationStatus: 'pending'
    }
  };
  const body = new FormData();
  body.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  body.append('file', new Blob([JSON.stringify(json)], { type: 'application/json;charset=utf-8' }));
  const response = await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,resourceKey', { method: 'POST', body });
  const file = await response.json();
  await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(file.id)}/permissions`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'anyone', role: 'reader' })
  });
  return file;
}

export async function createPrivateQuestionBankFile(name, json) {
  const options = typeof name === 'object' ? name : { name, json };
  const folderId = await questionBankFolder(options.subject, options.grade);
  const metadata = {
    name: options.name,
    mimeType: 'application/json',
    parents: [folderId],
    appProperties: {
      app: 'question-banks',
      targetType: 'questionBank',
      ...(options.sourceBankId ? { sourceBankId: options.sourceBankId } : {}),
      ...(options.subject ? { subject: String(options.subject).slice(0, 124) } : {}),
      ...(options.grade ? { grade: String(options.grade) } : {})
    }
  };
  const body = new FormData();
  body.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  body.append('file', new Blob([JSON.stringify(options.json)], { type: 'application/json;charset=utf-8' }));
  const response = await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,modifiedTime,appProperties', { method: 'POST', body });
  const file = await response.json();
  recentPrivateQuestionBanks.set(file.id, file);
  return file;
}

export async function createPrivateQuestionBankFileFromBlob({ name, blob, sourceBankId, subject, grade }) {
  if (!(blob instanceof Blob)) throw new Error('private_question_bank_blob_required');
  const folderId = await questionBankFolder(subject, grade);
  const metadata = {
    name,
    mimeType: 'application/json',
    parents: [folderId],
    appProperties: {
      app: 'question-banks',
      targetType: 'questionBank',
      sourceBankId,
      subject: String(subject).slice(0, 124),
      grade: String(grade)
    }
  };
  const body = new FormData();
  body.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  body.append('file', blob, name);
  const response = await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,modifiedTime,appProperties', { method: 'POST', body });
  const file = await response.json();
  recentPrivateQuestionBanks.set(file.id, file);
  return file;
}

export async function copySharedQuestionBankFile({ fileId, resourceKey, name, sourceBankId, subject, grade }) {
  const folderId = await questionBankFolder(subject, grade);
  const metadata = {
    name,
    parents: [folderId],
    appProperties: {
      app: 'question-banks',
      targetType: 'questionBank',
      sourceBankId,
      subject: String(subject).slice(0, 124),
      grade: String(grade)
    }
  };
  const response = await driveFetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}/copy?fields=id,name,modifiedTime,appProperties`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(resourceKey ? { 'X-Goog-Drive-Resource-Keys': `${fileId}/${resourceKey}` } : {})
      },
      body: JSON.stringify(metadata)
    }
  );
  const file = await response.json();
  recentPrivateQuestionBanks.set(file.id, file);
  return file;
}

export async function downloadSharedQuestionBankFileBlob({ driveFileId, driveResourceKey }) {
  const response = await driveFetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(driveFileId)}?alt=media`,
    {
      headers: driveResourceKey
        ? { 'X-Goog-Drive-Resource-Keys': `${driveFileId}/${driveResourceKey}` }
        : {}
    }
  );
  return response.blob();
}

export async function listPrivateQuestionBankFiles() {
  const query = "trashed = false and mimeType = 'application/json' and appProperties has { key='app' and value='question-banks' }";
  const params = new URLSearchParams({
    q: query,
    orderBy: 'modifiedTime desc',
    pageSize: '100',
    spaces: 'drive',
    fields: 'files(id,name,modifiedTime,appProperties)'
  });
  const response = await driveFetch(`https://www.googleapis.com/drive/v3/files?${params}`);
  const files = ((await response.json()).files || []).filter(file => file.appProperties?.targetType === 'questionBank');
  const merged = new Map(files.map(file => [file.id, file]));
  recentPrivateQuestionBanks.forEach((file, id) => { if (!merged.has(id)) merged.set(id, file); });
  return [...merged.values()].sort((left, right) => String(right.modifiedTime || '').localeCompare(String(left.modifiedTime || '')));
}

export async function downloadPrivateQuestionBankFile(fileId) {
  const response = await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media`);
  try {
    return await response.json();
  } catch (_error) {
    throw new Error('private_question_bank_json_invalid');
  }
}

export async function overwritePrivateQuestionBankFile(fileId, json) {
  const folderId = await questionBankFolder(json.subject, json.grade);
  await moveFileToFolder(fileId, folderId, {
    app: 'question-banks', targetType: 'questionBank',
    subject: String(json.subject || '').slice(0, 124), grade: String(json.grade || '')
  });
  const response = await driveFetch(`https://www.googleapis.com/upload/drive/v3/files/${encodeURIComponent(fileId)}?uploadType=media&fields=id,name,modifiedTime,appProperties`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json;charset=utf-8' },
    body: JSON.stringify(json)
  });
  const file = await response.json();
  recentPrivateQuestionBanks.set(file.id, file);
  return file;
}

export async function trashPrivateQuestionBankFile(fileId) {
  await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=id,trashed`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trashed: true })
  });
  recentPrivateQuestionBanks.delete(fileId);
}

export async function findPendingPracticeFile(operationId) {
  const escaped = String(operationId).replace(/['\\]/g, '');
  const q = `trashed = false and appProperties has { key='operationId' and value='${escaped}' } and appProperties has { key='operationStatus' and value='pending' }`;
  const response = await driveFetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id,resourceKey)&pageSize=2`);
  const payload = await response.json();
  if (payload.files.length > 1) throw new Error('drive_duplicate_pending_files');
  return payload.files[0] || null;
}

export async function overwritePracticeFile(driveFileId, json) {
  const folderId = await publishedPracticeFolder(json.subject);
  await moveFileToFolder(driveFileId, folderId);
  await driveFetch(`https://www.googleapis.com/upload/drive/v3/files/${encodeURIComponent(driveFileId)}?uploadType=media`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json;charset=utf-8' }, body: JSON.stringify(json)
  });
}

export async function markPracticeFileCommitted(driveFileId) {
  await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(driveFileId)}?fields=id,resourceKey`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appProperties: { operationStatus: 'committed' } })
  });
}

export async function deleteDriveFile(driveFileId) {
  await driveFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(driveFileId)}`, { method: 'DELETE' });
}
